"use client";

import { CLIENT_getAllFutureShowsForComedian, ShowInfo } from "@/data/shows";
import {
  ClapboardIcon,
  MicrophoneIcon,
  PersonOutlineIcon,
  StackedCoinsIcon,
  TshirtIcon,
} from "@/components/common/icons";
import {
  ComedianWithSpecialsAndSocialMedia,
  deprecated_getComedianWithSpecialsAndSocialMedia,
} from "@/data/comedians";
import { ContentNavEvents, MerchEvents, trackEvent } from "@/analytics";
import { useEffect, useState } from "react";
import { useParams, usePathname } from "next/navigation";

import { IconProps } from "@punchup/ui-common/components/common/icons";
import Link from "next/link";
import { cn } from "@/components/common/utils";
import { useExperiment } from "statsig-react";
import { usePageScroll } from "../providers/PageScrollProvider";

export interface ComedianPageNavItem {
  id: string;
  label: string;
  onClick?: () => void;
  url?: string;
  eventName: string;
  Icon: React.FC<IconProps>;
  disabled?: boolean;
  active: boolean;
}

export default function ComedianPageNav({
  className,
  listClassName,
  itemClassName,
  iconClassName,
}: {
  className?: string;
  listClassName?: string;
  itemClassName?: string;
  iconClassName?: string;
}) {
  const { comedianSlug, specialSlug } = useParams();
  const pathname = usePathname();
  const {
    scrollToContent,
    scrollToTour,
    scrollToBio,
    scrollToTop,
    scrollToMerch,
  } = usePageScroll();
  const [comedian, setComedian] = useState<
    ComedianWithSpecialsAndSocialMedia | undefined
  >(undefined);
  const [comedianShows, setComedianShows] = useState<null | ShowInfo[]>(null);
  const subscribeExperiment = useExperiment(
    "comedian-page-subscribe-for-shows"
  );
  const subscribeExperimentEnabled = subscribeExperiment.config.get(
    "enabled",
    false
  );

  // fetch comedian and comedian's show on mount
  useEffect(() => {
    const fetchComedianAndShows = async () => {
      if (comedianSlug) {
        // This really needs to be deprecated. We should have a way better way to check if comedian has content
        // rather than calling this expensive (and not cached) function.
        const comedianData =
          await deprecated_getComedianWithSpecialsAndSocialMedia(
            comedianSlug as string
          );
        if (!comedianData) {
          // Comedian not found, but page will likely 404 before here anyway
          return;
        }
        setComedian(comedianData);

        const shows = await CLIENT_getAllFutureShowsForComedian(
          comedianData.id
        );
        setComedianShows(shows);
      }
    };

    fetchComedianAndShows();
  }, [comedianSlug]);

  if (!comedianSlug) return null;

  const pathSegments = pathname.split("/").filter((segment) => segment !== "");

  // are we on a comedian's page? e.g. "/comedian-slug"
  const isComedianPage = comedianSlug && pathSegments.length === 1;

  // are we on a comedian's special page? e.g. "/comedian-slug/special-slug"
  const isComedianSpecialPage =
    comedianSlug && specialSlug && pathSegments.length === 2;

  // are we on a comedian's ticket page? e.g. "/comedian-slug/tickets"
  const isComedianTicketPage =
    comedianSlug &&
    pathSegments.includes("tickets") &&
    pathSegments.length === 2;

  const isComedianMerchPage =
    comedianSlug && pathSegments.includes("merch") && pathSegments.length === 2;

  // only display the nav bar on comedian pages
  const displayComedianNavbar =
    isComedianPage ||
    isComedianSpecialPage ||
    isComedianTicketPage ||
    isComedianMerchPage;

  if (!displayComedianNavbar) return null;

  const tipUrl = comedian?.tipJarLink;
  const merchUrl = comedian?.merchStoreLink;

  const comedianHasContent =
    comedian && comedian.specials && comedian.specials.length < 1;

  const comedianPageNavItems: ComedianPageNavItem[] = [
    {
      id: "content",
      label: "Content",
      onClick: comedianHasContent ? scrollToContent : scrollToTop,
      eventName: ContentNavEvents.NAVIGATE_TO_CONTENT,
      Icon: ClapboardIcon,
      disabled: false, // always show "Content", but change behavior based on whether comedian has content (above)
      active: false,
    },
    {
      id: "tour",
      label: "Tour",
      onClick: scrollToTour,
      eventName: ContentNavEvents.NAVIGATE_TO_TOUR_DATES,
      Icon: MicrophoneIcon,
      disabled: subscribeExperimentEnabled
        ? false // the experiment always shows the "Tour" tab, even when there are no shows
        : !comedianShows || comedianShows.length < 1, // the original logic
      active: false,
    },
    {
      id: "bio",
      label: "Bio",
      onClick: scrollToBio,
      eventName: ContentNavEvents.NAVIGATE_TO_BIO,
      Icon: PersonOutlineIcon,
      disabled: !comedian || !comedian.about || comedian.about.length < 1,
      active: false,
    },
    {
      id: "tip",
      label: "Tip",
      url: tipUrl ? tipUrl : undefined,
      eventName: ContentNavEvents.NAVIGATE_TO_TIP_COMEDIAN,
      Icon: StackedCoinsIcon,
      disabled: !tipUrl,
      active: false,
    },
    {
      id: "merch",
      label: "Merch",
      url: merchUrl ?? undefined,
      eventName: MerchEvents.NAVIGATE_TO_MERCH_STORE,
      Icon: TshirtIcon,
      disabled: !merchUrl || comedian?.shopifyMerchCollectionId != null,
      active: false,
    },
  ];

  comedianPageNavItems.push({
    id: "merch-store",
    label: "Merch",
    onClick: scrollToMerch,
    eventName: MerchEvents.NAVIGATE_TO_MERCH_STORE_SHOPIFY,
    Icon: TshirtIcon,
    disabled: !comedian || !comedian?.shopifyMerchCollectionId,
    active: false,
  });

  const enabledNavItems = comedianPageNavItems.filter((item) => !item.disabled);

  if (enabledNavItems.length === 0) return null;
  return (
    <nav
      className={cn(
        "w-full filter drop-shadow-secondary-nav bg-ivory overflow-hidden border-t border-t-[#e7dac7]",
        className
      )}
    >
      <ul
        className={cn(
          "grid items-center justify-between w-full grid-cols-5",
          listClassName
        )}
        style={{
          gridTemplateColumns: `repeat(${enabledNavItems.length}, minmax(0, 1fr))`,
        }}
      >
        {enabledNavItems.map(
          ({ id, label, Icon, onClick, url, eventName, disabled, active }) => (
            <li key={id} className="flex flex-row justify-center group">
              {disabled || !url ? (
                <button
                  type="button"
                  onClick={() => {
                    trackEvent(eventName, { disabled });
                    !disabled && onClick && onClick();
                  }}
                  disabled={disabled}
                  className={cn(
                    "w-full flex flex-col py-2 items-center justify-center gap-1 text-xs font-medium text-slate-900 scroll-smooth disabled:opacity-50 disabled:cursor-not-allowed disabled:underline-none transition-colors ease-in-out duration-200 md:hover:text-primary active:text-primary active:font-semibold focus:text-primary focus:font-semibold",
                    active && "text-primary font-semibold",
                    itemClassName
                  )}
                >
                  <Icon
                    className={cn(
                      "w-5 h-5 xs:w-6 xs:h-6 rounded-md text-inherit",
                      iconClassName
                    )}
                  />
                  <p className="leading-none">{label}</p>
                </button>
              ) : (
                <Link
                  href={url}
                  target="_blank"
                  onClick={() => {
                    trackEvent(eventName, { disabled });
                    onClick && onClick();
                  }}
                  className={cn(
                    "w-full py-2 flex flex-col items-center justify-center gap-1 text-xs font-medium text-slate-900 scroll-smooth md:hover:text-primary transition-colors ease-in-out duration-200 active:text-primary active:font-semibold focus:text-primary focus:font-semibold",
                    disabled ? "opacity-50 cursor-not-allowed" : "",
                    itemClassName
                  )}
                >
                  <Icon
                    className={cn(
                      "w-5 h-5 xs:w-6 xs:h-6 rounded-md text-inherit",
                      iconClassName
                    )}
                  />
                  <p className="leading-none">{label}</p>
                </Link>
              )}
            </li>
          )
        )}
      </ul>
    </nav>
  );
}
